import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Save, Shield, Lock } from "lucide-react";

// Canonical permission catalog — grouped for UI clarity.
const CATALOG = [
  { group: "Plans", perms: [
    { key: "plans.create", label: "Create plans" },
    { key: "plans.edit", label: "Edit any plan" },
    { key: "plans.edit_all", label: "Edit any (not just own)" },
    { key: "plans.delete_own", label: "Delete own plans" },
    { key: "plans.delete", label: "Delete any plan" },
    { key: "plans.publish", label: "Publish plans" },
    { key: "plans.view_stats", label: "View plan analytics" },
  ]},
  { group: "Uploads & Media", perms: [
    { key: "uploads.create", label: "Upload files" },
  ]},
  { group: "AI", perms: [
    { key: "ai.generate", label: "Use AI description generator" },
  ]},
  { group: "Categories & Collections", perms: [
    { key: "categories.manage", label: "Manage categories" },
    { key: "collections.manage", label: "Manage collections" },
  ]},
  { group: "Content", perms: [
    { key: "blogs.manage", label: "Manage blog posts" },
    { key: "seo.manage", label: "Manage SEO" },
  ]},
  { group: "Enquiries", perms: [
    { key: "inquiries.view", label: "View enquiries" },
    { key: "inquiries.reply", label: "Reply to enquiries" },
  ]},
  { group: "Payments & Customers", perms: [
    { key: "payments.view", label: "View payments" },
    { key: "payments.refund", label: "Issue refunds" },
    { key: "customers.view_pii", label: "View customer PII (email/phone)" },
  ]},
  { group: "Users & System", perms: [
    { key: "users.create", label: "Create users" },
    { key: "users.edit", label: "Edit users" },
    { key: "users.reset_password", label: "Reset passwords" },
    { key: "users.deactivate", label: "Deactivate users" },
    { key: "activity_log.view", label: "View activity log" },
  ]},
];

const ROLE_ORDER = ["super_admin", "admin", "team_manager", "content_writer", "sales"];

const hasPermission = (perms, key) => {
  if (!perms) return false;
  if (perms.includes("*")) return true;
  if (perms.includes(key)) return true;
  // Wildcard match e.g. "plans.*"
  const [ns] = key.split(".");
  if (perms.includes(`${ns}.*`)) return true;
  return false;
};

const RolePermissions = () => {
  const [roles, setRoles] = useState([]);
  const [drafts, setDrafts] = useState({}); // {role_name: [perm...]}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/roles");
        setRoles(data);
        const d = {};
        data.forEach((r) => { d[r.role] = [...(r.permissions || [])]; });
        setDrafts(d);
      } catch (e) {
        toast.error("Failed to load roles", { description: e.response?.data?.detail || "" });
      } finally {
        setLoading(false);
      }
    })();
    // Load once on mount. `api` + `toast` are stable module singletons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (role, perm) => {
    if (role === "super_admin") return; // locked
    setDrafts((prev) => {
      const cur = new Set(prev[role] || []);
      if (cur.has(perm)) cur.delete(perm);
      else cur.add(perm);
      return { ...prev, [role]: Array.from(cur) };
    });
  };

  const save = async (role) => {
    if (role === "super_admin") return;
    setSaving((s) => ({ ...s, [role]: true }));
    try {
      await api.put(`/admin/roles/${role}`, { permissions: drafts[role] });
      toast.success(`${role} permissions saved`);
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || "" });
    } finally {
      setSaving((s) => ({ ...s, [role]: false }));
    }
  };

  const visibleRoles = ROLE_ORDER.map((r) => roles.find((x) => x.role === r)).filter(Boolean);

  if (loading) return <AdminLayout title="Roles & Permissions"><div className="text-muted-foreground">Loading roles…</div></AdminLayout>;

  return (
    <AdminLayout title="Roles & Permissions">
      <div className="max-w-6xl">
        <div className="border border-border bg-card p-4 mb-6 text-sm flex items-start gap-3">
          <Shield size={16} className="text-accent mt-1" />
          <div>
            <div className="font-medium">Role-based access control</div>
            <div className="text-xs text-muted-foreground mt-1">Toggle permissions per role. Super Admin is locked with full access — this is intentional and cannot be modified.</div>
          </div>
        </div>

        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="rbac-matrix">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 sticky left-0 bg-card z-10 min-w-[280px]">Permission</th>
                {visibleRoles.map((r) => (
                  <th key={r.role} className="px-3 py-3 text-center min-w-[120px]" data-testid={`col-role-${r.role}`}>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{r.role.replace("_", " ")}</div>
                    {r.role === "super_admin" && <Lock size={12} className="mx-auto mt-1 text-muted-foreground" />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CATALOG.map((grp) => (
                <React.Fragment key={grp.group}>
                  <tr className="bg-background/60">
                    <td colSpan={visibleRoles.length + 1} className="px-4 py-2 overline text-accent">{grp.group}</td>
                  </tr>
                  {grp.perms.map((p) => (
                    <tr key={p.key} className="border-t border-border hover:bg-background/30" data-testid={`row-perm-${p.key}`}>
                      <td className="px-4 py-3 sticky left-0 bg-card z-10">
                        <div className="text-sm">{p.label}</div>
                        <code className="text-[10px] text-muted-foreground">{p.key}</code>
                      </td>
                      {visibleRoles.map((r) => {
                        const locked = r.role === "super_admin";
                        const val = locked ? true : hasPermission(drafts[r.role], p.key);
                        return (
                          <td key={r.role} className="px-3 py-3 text-center">
                            <Checkbox
                              data-testid={`cell-${r.role}-${p.key}`}
                              checked={val}
                              disabled={locked}
                              onCheckedChange={() => toggle(r.role, p.key)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {visibleRoles.filter((r) => r.role !== "super_admin").map((r) => (
            <Button
              key={r.role}
              data-testid={`save-role-${r.role}`}
              onClick={() => save(r.role)}
              disabled={!!saving[r.role]}
              className="rounded-none bg-accent hover:bg-accent/90"
            >
              <Save size={14} className="mr-2" /> {saving[r.role] ? "Saving…" : `Save ${r.role.replace("_", " ")}`}
            </Button>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default RolePermissions;
